import type {
  AdvisorInput,
  AdvisorRecommendation,
  Notice,
  NoticeDefinitionOption,
  NoticePayload,
  NoticeStatus,
  NoticeStep,
  NoticeStepPayload,
} from "@/features/notices/types";
import { apiFetch } from "@/lib/api";

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

interface ApiNoticeStep {
  id: string;
  description: string;
  position: number;
  due_date: string | null;
  is_completed: boolean;
  completed_at: string | null;
}

interface ApiNotice {
  id: string;
  client: string;
  client_name: string;
  case: string | null;
  notice_definition: string;
  notice_code: string;
  notice_title: string;
  severity: string;
  category: string;
  recommended_action: string;
  received_on: string;
  response_deadline: string | null;
  days_until_deadline: number | null;
  status: NoticeStatus;
  status_display: string;
  tax_year: string;
  notes: string;
  assigned_practitioner: string | null;
  assigned_practitioner_name: string;
  document: string | null;
  steps: ApiNoticeStep[];
  open_step_count: number;
  created_at: string;
  updated_at: string;
}

function toStep(a: ApiNoticeStep): NoticeStep {
  return {
    id: a.id,
    description: a.description,
    position: a.position,
    dueDate: a.due_date,
    isCompleted: a.is_completed,
    completedAt: a.completed_at,
  };
}

function toNotice(a: ApiNotice): Notice {
  return {
    id: a.id,
    client: a.client,
    clientName: a.client_name,
    case: a.case,
    noticeDefinition: a.notice_definition,
    noticeCode: a.notice_code,
    noticeTitle: a.notice_title,
    severity: a.severity,
    category: a.category,
    recommendedAction: a.recommended_action,
    receivedOn: a.received_on,
    responseDeadline: a.response_deadline,
    daysUntilDeadline: a.days_until_deadline,
    status: a.status,
    statusDisplay: a.status_display,
    taxYear: a.tax_year,
    notes: a.notes,
    assignedPractitioner: a.assigned_practitioner,
    assignedPractitionerName: a.assigned_practitioner_name,
    document: a.document,
    steps: a.steps.map(toStep),
    openStepCount: a.open_step_count,
    createdAt: a.created_at,
    updatedAt: a.updated_at,
  };
}

const NOTICES_PAGE = "?page_size=100";

/** GET /notices/?client= */
export async function listClientNotices(clientId: string): Promise<Notice[]> {
  const body = await apiFetch<Envelope<ApiNotice[]>>(
    `/notices/${NOTICES_PAGE}&client=${clientId}`,
  );
  return body.data.map(toNotice);
}

/**
 * Count the firm's un-resolved (OPEN + IN_PROGRESS) IRS notices for the
 * dashboard KPI. Uses the list endpoint's pagination `meta.count` so we don't
 * pull every row just to size the badge.
 */
export async function countPendingNotices(firm?: string): Promise<number> {
  const firmQ = firm ? `&firm=${encodeURIComponent(firm)}` : "";
  const results = await Promise.all(
    (["OPEN", "IN_PROGRESS"] as const).map((status) =>
      apiFetch<Envelope<ApiNotice[]> & { meta?: { count?: number } }>(
        `/notices/?page_size=1&status=${status}${firmQ}`,
      ),
    ),
  );
  return results.reduce((sum, body) => sum + (body.meta?.count ?? body.data.length), 0);
}

/** GET /notices/:id/ */
export async function getNotice(id: string): Promise<Notice> {
  const body = await apiFetch<Envelope<ApiNotice>>(`/notices/${id}/`);
  return toNotice(body.data);
}

/** POST /notices/ */
export async function createNotice(
  clientId: string,
  payload: NoticePayload,
): Promise<Notice> {
  const body = await apiFetch<Envelope<ApiNotice>>("/notices/", {
    method: "POST",
    json: { ...payload, client: clientId },
  });
  return toNotice(body.data);
}

/** PATCH /notices/:id/ */
export async function updateNotice(id: string, payload: NoticePayload): Promise<Notice> {
  const body = await apiFetch<Envelope<ApiNotice>>(`/notices/${id}/`, {
    method: "PATCH",
    json: payload,
  });
  return toNotice(body.data);
}

/** DELETE /notices/:id/ */
export function deleteNotice(id: string): Promise<void> {
  return apiFetch<void>(`/notices/${id}/`, { method: "DELETE" });
}

// --- notice steps -----------------------------------------------------------

export async function createNoticeStep(
  noticeId: string,
  payload: NoticeStepPayload,
): Promise<NoticeStep> {
  const body = await apiFetch<Envelope<ApiNoticeStep>>(`/notices/${noticeId}/steps/`, {
    method: "POST",
    json: payload,
  });
  return toStep(body.data);
}

export async function updateNoticeStep(
  noticeId: string,
  id: string,
  payload: NoticeStepPayload,
): Promise<NoticeStep> {
  const body = await apiFetch<Envelope<ApiNoticeStep>>(
    `/notices/${noticeId}/steps/${id}/`,
    { method: "PATCH", json: payload },
  );
  return toStep(body.data);
}

export function deleteNoticeStep(noticeId: string, id: string): Promise<void> {
  return apiFetch<void>(`/notices/${noticeId}/steps/${id}/`, { method: "DELETE" });
}

/** POST /notices/:id/steps/reorder/ — `ids` in the new order; returns the
 * updated notice (with steps re-sorted). */
export async function reorderNoticeSteps(
  noticeId: string,
  ids: string[],
): Promise<Notice> {
  const body = await apiFetch<Envelope<ApiNotice>>(
    `/notices/${noticeId}/steps/reorder/`,
    { method: "POST", json: { ids } },
  );
  return toNotice(body.data);
}

/** POST /notices/:id/apply-template/ — replace the notice's steps with the
 * firm template for `code`. */
export async function applyNoticeTemplate(
  noticeId: string,
  code: string,
): Promise<Notice> {
  const body = await apiFetch<Envelope<ApiNotice>>(
    `/notices/${noticeId}/apply-template/`,
    { method: "POST", json: { code } },
  );
  return toNotice(body.data);
}

export interface OpenCasePayload {
  resolution_type: string;
  assigned_practitioner?: string | null;
  step_ids?: string[];
}

/** POST /notices/:id/open-case/ — create a linked case seeded with steps.
 * Returns the new case id so the caller can navigate to it. */
export async function openCaseFromNotice(
  noticeId: string,
  payload: OpenCasePayload,
): Promise<{ id: string }> {
  const body = await apiFetch<Envelope<{ id: string }>>(
    `/notices/${noticeId}/open-case/`,
    { method: "POST", json: payload },
  );
  return { id: body.data.id };
}

// --- advisor ----------------------------------------------------------------

interface ApiRecommendation {
  resolution_type: string | null;
  terminal_phase: string;
  blocked: boolean;
  opens_case: boolean;
  suggested_notice_code: string | null;
  owed_amount_cents: number | null;
  owed_range: number | null;
  ia_payment_options: {
    label: string;
    months: number | null;
    monthly_payment_cents: number | null;
    requires_financials: boolean;
  }[];
  warnings: string[];
  messages: string[];
}

/** POST /notices/advisor/recommend/ — run the advisor branch server-side. */
export async function recommendResolution(
  input: AdvisorInput,
): Promise<AdvisorRecommendation> {
  const body = await apiFetch<Envelope<ApiRecommendation>>(
    "/notices/advisor/recommend/",
    { method: "POST", json: input },
  );
  const d = body.data;
  return {
    resolutionType: d.resolution_type,
    terminalPhase: d.terminal_phase,
    blocked: d.blocked,
    opensCase: d.opens_case,
    suggestedNoticeCode: d.suggested_notice_code,
    owedAmountCents: d.owed_amount_cents,
    owedRange: d.owed_range,
    iaPaymentOptions: d.ia_payment_options.map((o) => ({
      label: o.label,
      months: o.months,
      monthlyPaymentCents: o.monthly_payment_cents,
      requiresFinancials: o.requires_financials,
    })),
    warnings: d.warnings,
    messages: d.messages,
  };
}

// --- notice-definition registry ---------------------------------------------

interface ApiNoticeDefinition {
  id: string;
  code: string;
  title: string;
  severity: string;
  category: string;
  recommended_action: string;
}

/** GET /reference/notices/ — the IRS notice-code registry (optional keyword
 * search over code/title/description, used by the advisor + template manager). */
export async function listNoticeDefinitions(
  search?: string,
): Promise<NoticeDefinitionOption[]> {
  const q = search ? `&search=${encodeURIComponent(search)}` : "";
  const body = await apiFetch<Envelope<ApiNoticeDefinition[]>>(
    `/reference/notices/?page_size=200${q}`,
  );
  return body.data.map((d) => ({
    id: d.id,
    code: d.code,
    title: d.title,
    severity: d.severity,
    category: d.category,
    recommendedAction: d.recommended_action,
  }));
}
